import { Collection } from 'firestorter';
import firebase from 'firebase/app';

import { firestorePaths } from '../utils/paths';
import BaseDocument from './BaseDocument';

const COLLECTION_PATH = firestorePaths.LEAD_REENGAGEMENT_SEQUENCES;

/**
 * A coach's Lost & Disqualified Lead Re-Engagement sequence: eligibility settings and an ordered
 * list of day-offset blasts, each carrying its own DPL. Authored by a CAM in the Mass Text Outreach
 * → Re-Engagement tab. Stored as an auto-id doc with a `coachId` field (rather than keyed by
 * coachId) so a coach can have more than one sequence in the future.
 */
class LeadReengagementSequence extends BaseDocument {
  constructor(id, opts) {
    super(`${COLLECTION_PATH}/${id}`, opts);
  }

  get coachId() {
    return this.data.coachId;
  }

  get active() {
    return this.data.active;
  }

  get eligibility() {
    return this.data.eligibility;
  }

  get blasts() {
    return this.data.blasts;
  }

  /**
   * Loads the coach's sequence, if any.
   * @param {string} coachId
   * @returns {Promise<{ id: string, ...data } | null>}
   */
  static async getByCoach(coachId) {
    if (!coachId) return null;
    const collection = new Collection(COLLECTION_PATH, {
      createDocument: (src, opts) => new LeadReengagementSequence(src.id, {
        ...opts,
        disableObserverRefCount: true,
      }),
      query: (ref) => ref.where('coachId', '==', coachId).limit(1),
    });
    await LeadReengagementSequence.initCollection(collection);
    const doc = collection.docs[0];
    return doc ? { id: doc.id, ...doc.data } : null;
  }

  /**
   * Upserts the coach's sequence config. Edits the existing doc in place when `id` is provided,
   * otherwise creates a new auto-id doc. Clears the legacy sequence-level `dpl` field, which each
   * blast now carries for itself.
   * @param {object} params
   * @param {string} [params.id] Existing sequence doc id.
   * @param {string} params.coachId
   * @param {boolean} params.active
   * @param {object} params.eligibility { salesStages, countries, requireConsent }
   * @param {Array} params.blasts [{ blastId, dayOffset, bodyTemplate, dpl, image? }] where `dpl` is
   *   the blast's direct purchase link URL and `image` is { url, fileName, fileSize } of an
   *   uploaded Brevo image, omitted when the blast has none.
   * @param {string} params.userId The CAM performing the edit.
   * @returns {Promise<string>} The sequence doc id.
   */
  static async upsert({
    id,
    coachId,
    active,
    eligibility,
    blasts,
    userId,
  }) {
    const now = firebase.firestore.FieldValue.serverTimestamp();
    const fields = {
      coachId,
      active,
      eligibility,
      blasts,
      updatedAt: now,
      updatedBy: userId,
    };

    if (id) {
      const sequence = new LeadReengagementSequence(id, { disableObserverRefCount: true });
      await sequence.init();
      await sequence.updateFields({ ...fields, dpl: firebase.firestore.FieldValue.delete() });
      return id;
    }

    const collection = new Collection(COLLECTION_PATH);
    const created = await collection.add({
      ...fields,
      createdAt: now,
      createdBy: userId,
    });
    return created.id;
  }
}

export default LeadReengagementSequence;
